/**
 * Session results → Catchball bridge.
 *
 * Completing a session does not auto-package it for pass-up. This card lets the
 * facilitator attach the result to the matching Catchball loop (when missing)
 * and jump back to the desk with include-for-send context.
 *
 * Important: V3 / live runs sometimes persist `cycle_id` without `track_id`.
 * That used to make this card return null with no explanation. We always show
 * a card for completed cycle-linked sittings and backfill track from the loop
 * on attach.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Link2, Loader2 } from 'lucide-react';
import { buildCatchballInboxUrl } from '../../../lib/catchballInboxUrls';
import {
  linkSessionToCatchballLoop,
  resolveLoopIdForSession,
} from '../../../lib/catchballSessionLink';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useToast } from '../../ui/Toast';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { normalizeLoopState } from '../../../types/prioritization/catchballLoop';

export function AttachSessionToBallCard({
  session,
  urlLoopId,
  urlBallId,
}: {
  session: PrioritizationSession;
  urlLoopId?: string | null;
  urlBallId?: string | null;
}) {
  const { showToast } = useToast();
  const loops = useCatchballLoopStore((s) => s.loops);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const linkSessionToTrack = useSessionStore((s) => s.linkSessionToTrack);
  const [busy, setBusy] = useState(false);
  const [loopsLoading, setLoopsLoading] = useState(false);

  const complete = session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED';

  useEffect(() => {
    if (!complete || !session.cycleId) return;
    let cancelled = false;
    setLoopsLoading(true);
    void fetchLoops(session.cycleId).finally(() => {
      if (!cancelled) setLoopsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [complete, session.cycleId, fetchLoops]);

  const resolvedLoopId = useMemo(
    () =>
      resolveLoopIdForSession({
        urlLoopId,
        sessionId: session.id,
        trackId: session.trackId,
        cycleId: session.cycleId,
        loops,
      }),
    [urlLoopId, session.id, session.trackId, session.cycleId, loops],
  );

  const loop = useMemo(
    () => (resolvedLoopId ? loops.find((l) => l.id === resolvedLoopId) ?? null : null),
    [loops, resolvedLoopId],
  );

  const alreadyLinked = Boolean(loop?.sessionIds?.includes(session.id));
  const missingTrack = !session.trackId;

  if (!complete) return null;

  if (!session.cycleId) {
    return (
      <section className="mb-6 border border-[var(--mustard)] dark:border-[var(--mustard)]/50 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 p-4 space-y-2">
        <h2 className="text-sm font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]">
          Not linked to a cycle
        </h2>
        <p className="text-sm text-[var(--mustard)]/90 dark:text-[var(--mustard)]/90">
          This session finished, but it has no cycle — so there is no Catchball ball to attach it to.
        </p>
      </section>
    );
  }

  if (loopsLoading && !loop) {
    return (
      <section className="mb-6 border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)]/70 dark:bg-[var(--slate)]/25 p-4">
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Looking for a Catchball on this cycle…
        </p>
      </section>
    );
  }

  if (!loop) {
    return (
      <section className="mb-6 border border-[var(--mustard)] dark:border-[var(--mustard)]/50 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 p-4 space-y-2">
        <h2 className="text-sm font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]">
          No ball on this cycle yet
        </h2>
        <p className="text-sm text-[var(--mustard)]/90 dark:text-[var(--mustard)]/90">
          {missingTrack
            ? 'This session also has no track assigned, so we could not match it to a Catchball automatically.'
            : 'There is no open Catchball loop on this track to attach to.'}{' '}
          Open Catchball for the cycle and take or create a ball first.
        </p>
        <Link
          to={`/prioritization/catchball?cycleId=${session.cycleId}`}
          className="inline-flex text-sm font-medium text-[var(--mustard)] dark:text-[var(--mustard)] underline hover:no-underline"
        >
          Open Catchball →
        </Link>
      </section>
    );
  }

  const inboxHref = buildCatchballInboxUrl(loop.cycleId || session.cycleId, loop.id, {
    ballId: urlBallId ?? undefined,
  });
  const inboxPassUpHref = `${inboxHref}#inbox-send`;
  const loopOpen = normalizeLoopState(loop.state) === 'OPEN';

  const handleAttach = async () => {
    setBusy(true);
    try {
      if (session.cycleId) {
        await fetchLoops(session.cycleId);
      }

      // Backfill track when the live run never stamped track_id (common V3 gap).
      if (!session.trackId && loop.trackId) {
        const linked = await linkSessionToTrack(session.id, loop.trackId);
        if (!linked.success) {
          showToast(
            'warning',
            'Attached without track link',
            linked.error ??
              'Could not set the session track. You can still open Catchball — we will try to show the session there.',
          );
        }
      }

      if (!alreadyLinked) {
        const ok = await linkSessionToCatchballLoop(loop.id, session.id);
        if (!ok) {
          showToast(
            'error',
            'Could not attach session',
            'Open Catchball anyway and use Include in next send if the session appears on the track list.',
          );
        } else {
          showToast('success', 'Attached to Catchball', 'Included for the next pass-up — open the Inbox to send.');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-6 border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)]/70 dark:bg-[var(--slate)]/25 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <ArrowUp className="w-4 h-4 mt-0.5 text-[var(--text-ter)] dark:text-[var(--text-ter)] shrink-0" />
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {alreadyLinked ? 'Ready for Catchball' : 'Attach to this ball'}
          </h2>
          <p className="text-sm text-[var(--text-ter)]/85 dark:text-[var(--text-ter)]/85 mt-0.5">
            {alreadyLinked
              ? 'This result is on the Catchball. Confirm “Include in next send,” then Pass results up.'
              : missingTrack
                ? 'This ranking finished without a track stamp. Attach it to the Catchball ball on this cycle — we will place it on the ball’s track.'
                : 'Link this completed ranking to the Catchball ball for this track, then package it into the next pass-up.'}
            {!loopOpen ? ' (Loop is not open — pass-up may be unavailable.)' : null}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {!alreadyLinked && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleAttach()}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            Attach to Catchball
          </button>
        )}
        <Link
          to={inboxPassUpHref}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)]/80 dark:bg-[var(--surface)] text-[var(--text-ter)] dark:text-[var(--text-ter)] text-sm font-medium hover:bg-[var(--bg-raised)]"
        >
          <ArrowUp className="w-4 h-4" />
          Open Catchball · Pass up
        </Link>
      </div>
    </section>
  );
}
